import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@wix/design-system';
import QRCode from 'react-qr-code';
import classNames from 'classnames';
import { AppContextProvider } from '../../../AppLoader';
import { EnforcementCard } from '../EnforcementCard';
import { ButtonWithLoader } from '../../../ButtonWithLoader/ButtonWithLoader';
import { ThemedText, ThemedTextButton } from '../../../ThemedComponents';
import { CopyToClipboard } from './CopyToClipboard';
import s from '../modals.scss';

/**
 * redesigned authenticator-app "scan QR" first step (wix-2025).
 */
export const AuthenticatorAppQrStepRedesign: React.FC = observer(() => {
  const { t } = useTranslation();
  const {
    rootStore: {
      postLoginStore: { premiumUsers2FaEnforcementStore: store },
      displayStore: { preset },
    },
  } = useContext(AppContextProvider);
  const {
    authenticatorAppModals,
    multiFactorAuth,
    shouldActivateNewColorBranding,
  } = store;

  return (
    <EnforcementCard
      priority="secondary"
      title={authenticatorAppModals.qrModalTitle}
      subtitle={authenticatorAppModals.qrModalSubTitle}
      showBackButton
      backButtonCallback={store.goBackToEnforcementOrChooseBackupMethod}
      primaryButton={
        // onQrModalConfirm is `action` (not action.bound) — wrap to keep `this`.
        <ButtonWithLoader
          skin={preset.buttonDesign?.skin ?? 'standard'}
          onClick={() => authenticatorAppModals.onQrModalConfirm()}
        >
          {t('multiFactorAuth.addAuthApp.qrModal.confirmButton')}
        </ButtonWithLoader>
      }
    >
      <Box direction="vertical" width="100%" gap={3}>
        <ThemedText size="small">
          <Trans i18nKey="multiFactorAuth.addAuthApp.qrModal.content.line1">
            Scan the QR code to add Wix to your authenticator app:
          </Trans>
        </ThemedText>

        <Box direction="vertical" align="center" width="100%">
          <Tooltip
            maxWidth="219px"
            content={
              <ThemedText size="small" light>
                <Trans i18nKey="multiFactorAuth.addAuthApp.qrModal.qrSvg.tooltipContent">
                  Scan this code with an authenticator app such as Google
                  Authenticator, Authy, or others
                  <a
                    className={classNames(
                      s.inTextLink,
                      shouldActivateNewColorBranding && s.newColorsBranding,
                    )}
                    target="_blank"
                    onClick={() => multiFactorAuth.onClickLearnMoreLink()}
                    href={multiFactorAuth.authenticatorAppArticleLink}
                    rel="noreferrer"
                  >
                    Learn more about these apps
                  </a>
                </Trans>
              </ThemedText>
            }
          >
            <QRCode value={authenticatorAppModals.qrUrl} size={148} />
          </Tooltip>
        </Box>

        <Box direction="vertical" width="100%" gap={2}>
          <ThemedText size="small">
            {t('multiFactorAuth.addAuthApp.qrModal.secretKey.label')}
          </ThemedText>
          <CopyToClipboard
            inputText={authenticatorAppModals.sharedSecretString}
            copyText={t(
              'multiFactorAuth.addAuthApp.qrModal.secretKey.input.copyText',
            )}
            copiedText={t(
              'multiFactorAuth.addAuthApp.qrModal.secretKey.input.copiedText',
            )}
            onCopy={() => authenticatorAppModals.onCopyQrCodeClicked()}
          />
          <ThemedText size="small" secondary>
            <Trans i18nKey="multiFactorAuth.addAuthApp.qrModal.subtitle">
              Don't have an authenticator app?
              <ThemedTextButton
                as="a"
                size="small"
                skin="standard"
                target="_blank"
                onClick={() => multiFactorAuth.onClickLearnMoreLink()}
                href={multiFactorAuth.authenticatorAppArticleLink}
              >
                Find out how to install
              </ThemedTextButton>
            </Trans>
          </ThemedText>
        </Box>
      </Box>
    </EnforcementCard>
  );
});
